import React, { useContext } from "react";
import PurpleLine from "images/kosmofi/kosmofi-line.svg";
import BannerDesktop from "images/kosmofi/kosmofi-banner-desktop.png";
import BannerMobile from "images/kosmofi/kosmofi-banner-mobile.png";
import ImageBuildYourCreditScore from "images/kosmofi/kosmofi-build_your_credit_score.webp";
import ImageCustomizableSpendingPower from "images/kosmofi/kosmofi-customizable_spending_power.webp";
import ImageNoCreditHistory from "images/kosmofi/kosmofi-no_credit_history.webp";
import { BreakpointContext } from "components/common";
import BoostHeader from "components/BoostHeader";
import AutoplayCarousel from "common/AutoplayCarousel";
import TestimonialCard1 from "images/boost/boost_testimonial_card-1.webp";
import TestimonialCard2 from "images/boost/boost_testimonial_card-2.webp";
import TestimonialCard3 from "images/boost/boost_testimonial_card-3.webp";
import TestimonialCard4 from "images/boost/boost_testimonial_card-4.webp";
import { socialProofImages } from "pages/Boost/utils";
import LandingForm from "./LandingForm";

const KosmofiLanding = () => {
  const { isBelow } = useContext(BreakpointContext);
  const belowMd = isBelow("md");

  return (
    <>
      <div className="flex flex-col min-h-screen h-full lg:overflow-hidden lg:pb-0" style={{  }}>
        <div className="bg-black w-full">
          <BoostHeader />
        </div>
        <div className="relative flex flex-col pt-8 md:pt-16 lg:pt-0 lg:flex-row-reverse h-full items-start justify-start bg-black lg:min-h-[791px] overflow-hidden">
          <img
            src={BannerDesktop}
            alt="Kosmofi Background"
            className="hidden lg:block h-screen absolute top-0"
            style={{ height: "1500px", right: "-150px", top: "-250px" }}
          />
          <img
            src={BannerMobile}
            alt="Kosmofi Background"
            className="mb-8 sm:mb-16 block lg:hidden w-full transition-all duration-300 ease-in-out object-cover"
            style={{ minHeight: 190 }}
          />
          <div className="flex lg:self-center w-full justify-center mt-8 md:mt-12 lg:mt-0 mb-24 lg:justify-start lg:pl-24 lg:items-center">
            <LandingForm />
          </div>
        </div>
        <div className="bg-neutral-300 flex flex-col w-full">
          <div className="w-full grid place-items-center">
            {/* TESTIMONIAL CARDS SECTION */}
            <div
              className="mt-10 md:mt-24 flex flex-col justify-center items-center mx-auto"
              style={{ maxWidth: 1300 }}
            >
              <div className="font-bold text-2xl text-black">Hear what others have to say</div>
              <div className="flex overflow-x-scroll hide-x-scrollbar shrink-0 w-full">
                <div className="mx-6 md:mx-10 mt-5 pb-10 flex self-start overflow-visible">
                  {
                    [TestimonialCard1, TestimonialCard2, TestimonialCard3, TestimonialCard4]
                      .map((imageSrc, idx) => (
                        <img
                          alt=""
                          src={imageSrc}
                          // eslint-disable-next-line react/no-array-index-key
                          key={`testimonial-${idx}`}
                          style={{ width: 295, height: "auto" }}
                          className="pr-6 box-content transform hover:scale-110 transition-transform"
                        />
                      ))
                  }
                </div>
              </div>
            </div>
          </div>
          {/* SOCIAL PROOF CAROUSEL */}
          <div className="py-8 bg-neutral-300">
            <AutoplayCarousel
              titleText="As mentioned in"
              duplicationFactor={3}
              totalWidthPreload={7752} // will need to change this if adding/removing image
              items={socialProofImages.map((item) => {
                return (
                  <div className="" style={{ height: 30 }}>
                    <img src={item.img} alt={item.alt} className="" />
                  </div>
                );
              })}
            />
          </div>
        </div>
        {/* PAGE BREAK */}
        <div className="py-10 flex flex-col text-center justify-center items-center gap-4 bg-white text-black">
          <h2 className="font-bold text-center leading-tight px-10 md:px-0" style={{ letterSpacing: -0.25, maxWidth: 577, fontSize: belowMd ? 24 : 36 }}>
            Opens opportunities for every business owner to achieve more
          </h2>
          <img src={PurpleLine} alt="" className="mt-4" style={{ width: 198, height: "auto" }} />
        </div>
        <div className="bg-neutral-50 text-black">
          <div className="mx-auto px-6" style={{ maxWidth: belowMd ? 564 : 1151 }}>
            {/* NO CREDIT HISTORY */}
            <div
              className="py-8 md:py-0 grid grid-cols-1 md:grid-cols-2 md:gap-4 box-border mx-auto"
              style={{ height: belowMd ? undefined : 643 }}
            >
              <div className="flex justify-center items-center order-1 md:order-2">
                <img alt="" src={ImageNoCreditHistory} className="" style={{ maxWidth: belowMd ? 342 : 423, height: "auto", objectFit: "cover" }} />
              </div>
              <div className="flex justify-center items-center order-2 md:order-1">
                <div className="py-8 sm:px-8 md:px-10 md:pt-[60px] md:pb-[45px] flex-1 flex flex-col rounded-lg shrink-0 text-left">
                  <div className="font-bold leading-tight text-4xl">
                    No credit history required
                  </div>
                  <div className="mt-4" style={{ letterSpacing: 0.1 }}>You are worth more than just a number (a.k.a. an arbitrary credit score). We conduct individualized assessments based on financial history and other sources and use our proprietary algorithm to determine your credit worthiness.</div>
                </div>
              </div>
            </div>
            {/* BUILD YOUR CREDIT SCORE */}
            <div
              className="py-8 md:py-0 grid grid-cols-1 md:grid-cols-2 md:gap-4 box-border mx-auto"
              style={{ height: belowMd ? undefined : 643 }}
            >
              <div className="flex justify-center items-center order-1">
                <img alt="" src={ImageBuildYourCreditScore} className="w-full" style={{ maxWidth: belowMd ? 342 : 438, height: "auto", objectFit: "cover" }} />
              </div>
              <div className="flex justify-center items-center order-2">
                <div className="bg-neutral-50 py-8 sm:px-8 md:px-10 md:pt-[60px] md:pb-[45px] flex-1 flex flex-col rounded-lg shrink-0 text-left">
                  <div className="font-bold leading-tight text-4xl">
                    Build Your Credit Score
                  </div>
                  <div className="mt-4" style={{ letterSpacing: 0.1 }}>Through consistent on-time payments, your credit score will grow. Set up an &quot;optional&quot; 7 day pay to maximize this cycle and increase the rate in which your credit score can improve.</div>
                </div>
              </div>
            </div>
            {/* CUSTOMIZABLE SPENDING POWER */}
            <div
              className="py-8 md:py-0 grid grid-cols-1 md:grid-cols-2 md:gap-4 box-border mx-auto"
              style={{ height: belowMd ? undefined : 643 }}
            >
              <div className="flex justify-center items-center order-1 md:order-2">
                <img alt="" src={ImageCustomizableSpendingPower} className="w-full" style={{ maxWidth: 428, height: "auto", objectFit: "cover" }} />
              </div>
              <div className="flex justify-center items-center order-2 md:order-1">
                <div className="bg-neutral-50 py-8 sm:px-8 md:px-10 md:pt-[60px] md:pb-[45px] flex-1 flex flex-col rounded-lg shrink-0 text-left">
                  <div className="font-bold leading-tight text-4xl">
                    Customizable Spending Power
                  </div>
                  <div className="mt-4" style={{ letterSpacing: 0.1 }}>Since we do not rely on a credit score to determine your spending power, we have the flexibility to update your spending power in real time! Spending power ranges from $100 - $30,000.*</div>
                  <div className="mt-4 text-xs" style={{ letterSpacing: 0.1 }}>* Not all applicants will be approved for the maximum spending power.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default KosmofiLanding;
